import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { Plus, Trash2, X, Camera, User, Save, Phone } from 'lucide-react';
import { toast } from 'sonner';
import CupomCapture from '../../components/proposals/CupomCapture';
import SignaturePad from '../../components/proposals/SignaturePad';
import CpfCnpjInput from '../../components/CpfCnpjInput';
import CatalogItemModal from '../../components/CatalogItemModal';
import { mensagemCpfCnpj } from '../../utils/validators';

const fmt2 = (v) => Number(v || 0).toFixed(2).replace('.', ',');
const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const STATUS_OPTIONS = [
  { value: 'nao_enviado', label: 'Não enviado' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'pago', label: 'Pago' },
];

const TIPO_OPTIONS = [
  { value: 'orcamento', label: 'Orçamento' },
  { value: 'proposta', label: 'Proposta Técnica' },
  { value: 'contrato', label: 'Contrato' },
];

const PAGAMENTO_TIPOS = [
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_avista', label: 'Cartão à Vista' },
  { value: 'cartao_parcelado', label: 'Cartão Parcelado' },
  { value: 'boleto', label: 'Boleto' },
];

const emptyServiceItem = (pos) => ({ position: pos, descricao: '', quantidade: '', unidade: 'SV', valor_unitario: '', valor_total: 0, tipo: 'avulso', secao: 'servico', ref_id: null });
const emptyMaterialItem = (pos) => ({ position: pos, descricao: '', quantidade: '', unidade: 'UN', valor_unitario: '', valor_total: 0, tipo: 'avulso', secao: 'material', ref_id: null });
const isService = (i) => i.secao === 'servico' || i.tipo === 'servico';
const emptyClient = { nome: '', cpf_cnpj: '', tel: '', whatsapp: '', email: '', end: '', num: '', cep: '', cidade: '', uf: '' };
const emptyPagamento = { tipo: 'pix', parcelas: 1, vencimento_boleto: '' };

export default function ProposalForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState({
    titulo: '', tipo: 'orcamento', status: 'nao_enviado',
    emissao: new Date().toISOString().split('T')[0], validade: '',
    cliente_id: '', cliente_nome: '', cliente_dados: {}, obs: '', desconto: 0, itens: [], num: '',
    prazo_execucao: '', garantia_prazo: '', garantia_condicoes: '',
  });
  const [pagamento, setPagamento] = useState(emptyPagamento);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [newClient, setNewClient] = useState(emptyClient);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [itemSearch, setItemSearch] = useState({});
  const [showItemSearch, setShowItemSearch] = useState({});
  const [loading, setLoading] = useState(false);
  const [showCupom, setShowCupom] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [cupons, setCupons] = useState([]);
  const [proposalId, setProposalId] = useState(id || null);
  const [nextPos, setNextPos] = useState(1);
  const [garantia, setGarantia] = useState({ prazo: '', condicoes: '' });
  const [categories, setCategories] = useState([]);
  const [catalogModal, setCatalogModal] = useState({ open: false, pos: null, kind: 'produto', secao: 'material', descricao: '' });

  // Dirty tracking
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const dirtyReady = useRef(false);
  useEffect(() => { const t = setTimeout(() => { dirtyReady.current = true; }, 500); return () => clearTimeout(t); }, []);
  useEffect(() => { if (dirtyReady.current) setIsDirty(true); }, [form, pagamento]);
  useEffect(() => {
    const fn = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', fn);
    return () => window.removeEventListener('beforeunload', fn);
  }, [isDirty]);
  const safeNavigate = (path) => {
    if (isDirty) { setPendingNavPath(path); setShowUnsavedDialog(true); }
    else navigate(path);
  };
  const confirmLeave = () => { setIsDirty(false); setShowUnsavedDialog(false); navigate(pendingNavPath || '/'); };

  useEffect(() => { loadMasterData(); if (isEdit) loadProposal(); }, [id]);

  const loadMasterData = async () => {
    const [p, s, c, cfg] = await Promise.all([
      api.get('/products/').catch(() => ({ data: [] })),
      api.get('/services/').catch(() => ({ data: [] })),
      api.get('/clients/').catch(() => ({ data: [] })),
      api.get('/settings/').catch(() => ({ data: { empresa: {} } })),
    ]);
    setProducts(p.data); setServices(s.data); setClients(c.data);
    setCategories(cfg.data?.categorias || []);
    const emp = cfg.data?.empresa || {};
    setGarantia({ prazo: emp.garantia_prazo || '', condicoes: emp.garantia_condicoes || '' });
    // Pré-preenche os campos do form somente ao criar novo orçamento
    if (!isEdit) {
      setForm(prev => ({
        ...prev,
        garantia_prazo: emp.garantia_prazo || '',
        garantia_condicoes: emp.garantia_condicoes || '',
      }));
    }
  };

  const loadProposal = async () => {
    try {
      const res = await api.get(`/proposals/${id}`);
      const p = res.data;
      setForm({
        titulo: p.titulo, tipo: p.tipo, status: p.status,
        emissao: p.emissao || '', validade: p.validade || '',
        cliente_id: p.cliente_id || '', cliente_nome: p.cliente_nome || '',
        cliente_dados: p.cliente_dados || {}, obs: p.obs || '',
        desconto: p.desconto || 0, itens: p.itens || [], num: p.num || '',
        prazo_execucao: p.prazo_execucao || '',
        garantia_prazo: p.garantia_prazo || '',
        garantia_condicoes: p.garantia_condicoes || '',
      });
      if (p.forma_pagamento) setPagamento({ tipo: p.forma_pagamento.tipo || 'pix', parcelas: p.forma_pagamento.parcelas || 1, vencimento_boleto: p.forma_pagamento.vencimento_boleto || '' });
      setClientSearch(p.cliente_nome || '');
      setCupons(p.cupons_anexos || []);
      const maxPos = Math.max(0, ...(p.itens || []).map(i => i.position));
      setNextPos(maxPos + 1);
      // Itens antigos sem vínculo com o catálogo (avulso) já aparecem com o aviso ao abrir
      const seedSearch = {};
      (p.itens || []).forEach(i => { if (i.descricao?.trim() && !i.ref_id) seedSearch[i.position] = i.descricao; });
      setItemSearch(seedSearch);
    } catch { toast.error('Erro ao carregar orçamento'); navigate('/'); }
  };

  const totalServicos = form.itens.filter(isService).reduce((s, i) => s + (i.valor_total || 0), 0);
  const totalMateriais = form.itens.filter(i => !isService(i)).reduce((s, i) => s + (i.valor_total || 0), 0);
  const subtotal = totalServicos + totalMateriais;
  const total = Math.max(0, subtotal - form.desconto);
  const valorParcela = pagamento.tipo === 'cartao_parcelado' && pagamento.parcelas > 0 ? total / pagamento.parcelas : 0;

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Items ────────────────────────────────────────────────────────────────
  const addServiceItem = () => {
    const pos = nextPos; setNextPos(p => p + 1);
    setForm(p => ({ ...p, itens: [...p.itens, emptyServiceItem(pos)] }));
  };
  const addMaterialItem = () => {
    const pos = nextPos; setNextPos(p => p + 1);
    setForm(p => ({ ...p, itens: [...p.itens, emptyMaterialItem(pos)] }));
  };

  const updateItem = (pos, field, value) => {
    setForm(p => ({
      ...p,
      itens: p.itens.map(i => {
        if (i.position !== pos) return i;
        const u = { ...i, [field]: value };
        if (field === 'quantidade' || field === 'valor_unitario') {
          u.valor_total = (u.quantidade || 0) * (u.valor_unitario || 0);
        }
        if (field === 'descricao') {
          // Editar o texto manualmente invalida o vínculo com o item do catálogo selecionado antes
          u.ref_id = null;
          u.tipo = 'avulso';
        }
        return u;
      })
    }));
  };

  const removeItem = (pos) => setForm(p => ({ ...p, itens: p.itens.filter(i => i.position !== pos) }));

  const selectCatalogItem = (pos, item, type, secao) => {
    setForm(p => ({
      ...p,
      itens: p.itens.map(i => {
        if (i.position !== pos) return i;
        const qty = i.quantidade;
        const price = item.valor || 0;
        const total = (Number(qty) || 0) * price;
        return { ...i, descricao: (item.descricao || '').toUpperCase(), valor_unitario: price, valor_total: total, unidade: item.unidade || (secao === 'servico' ? 'SV' : 'UN'), tipo: type, secao, ref_id: item.id };
      })
    }));
    setShowItemSearch(p => ({ ...p, [pos]: false }));
    setItemSearch(p => ({ ...p, [pos]: '' }));
  };

  const getServiceResults = (pos) => {
    const q = (itemSearch[pos] || '').toLowerCase();
    if (!q) return [];
    return services.filter(s => s.descricao?.toLowerCase().includes(q)).slice(0, 8);
  };

  const getMaterialResults = (pos) => {
    const q = (itemSearch[pos] || '').toLowerCase();
    if (!q) return [];
    return products.filter(p => p.descricao?.toLowerCase().includes(q)).slice(0, 8);
  };

  const openCatalogModal = (pos, secao) => {
    const kind = secao === 'servico' ? 'servico' : 'produto';
    setCatalogModal({ open: true, pos, kind, secao, descricao: itemSearch[pos] || '' });
  };

  const saveCatalogItem = async (payload) => {
    const endpoint = catalogModal.kind === 'servico' ? '/services/' : '/products/';
    const res = await api.post(endpoint, payload);
    return res.data;
  };

  const handleCatalogSaved = (item) => {
    const { pos, kind, secao } = catalogModal;
    if (kind === 'servico') setServices(p => [...p, item]);
    else setProducts(p => [...p, item]);
    selectCatalogItem(pos, item, kind, secao);
  };

  // ── Client ─────────────────────────────────────────────────────────────
  const filteredClients = clients.filter(c =>
    c.nome?.toLowerCase().includes(clientSearch.toLowerCase()) || c.cpf_cnpj?.includes(clientSearch)
  ).slice(0, 8);

  const selectClient = (c) => {
    setClientSearch(c.nome);
    setForm(p => ({ ...p, cliente_id: c.id, cliente_nome: c.nome, cliente_dados: c }));
    setShowClientSuggestions(false); setShowNewClientForm(false);
  };

  const handleClientSearch = (val) => {
    const upVal = val.toUpperCase();
    setClientSearch(upVal);
    setForm(p => ({ ...p, cliente_nome: upVal, cliente_id: '', cliente_dados: {} }));
    setShowClientSuggestions(true);
    const exact = clients.find(c => c.nome.toLowerCase() === upVal.toLowerCase());
    if (!exact && upVal.length > 0) setShowNewClientForm(true);
    else setShowNewClientForm(false);
  };

  const tryContactPicker = async () => {
    if ('contacts' in navigator && 'ContactsManager' in window) {
      try {
        const contacts = await navigator.contacts.select(['name', 'tel'], { multiple: false });
        if (contacts.length > 0) {
          const contact = contacts[0];
          const name = contact.name?.[0] || '';
          const tel = (contact.tel?.[0] || '').replace(/\s+/g, '');
          handleClientSearch(name);
          setNewClient(p => ({ ...p, nome: name, tel, whatsapp: tel }));
        }
      } catch {}
    } else {
      toast.info('Busca na agenda não disponível neste browser. Use Chrome no Android.');
    }
  };

  const saveNewClient = async () => {
    if (!clientSearch && !newClient.nome) return;
    // Valida CPF/CNPJ se preenchido
    if (newClient.cpf_cnpj) {
      const erro = mensagemCpfCnpj(newClient.cpf_cnpj);
      if (erro) { toast.error(erro); return; }
    }
    try {
      const res = await api.post('/clients/', { ...newClient, nome: clientSearch || newClient.nome });
      const c = res.data;
      setClients(p => [...p, c]);
      selectClient(c);
      setNewClient(emptyClient);
      setShowNewClientForm(false);
      toast.success('Cliente cadastrado!');
    } catch { toast.error('Erro ao cadastrar cliente'); }
  };

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.titulo) { toast.error('Informe o título do orçamento'); return; }
    const semCadastro = form.itens.find(i => i.descricao?.trim() && !i.ref_id);
    if (semCadastro) {
      toast.error(`"${semCadastro.descricao}" precisa ser selecionado ou cadastrado no catálogo antes de salvar`);
      return;
    }
    setLoading(true);
    try {
      const fp = {
        tipo: pagamento.tipo,
        parcelas: (pagamento.tipo === 'cartao_parcelado' || pagamento.tipo === 'boleto') ? Number(pagamento.parcelas) : null,
        valor_parcela: (pagamento.tipo === 'cartao_parcelado' || pagamento.tipo === 'boleto') && pagamento.parcelas > 0 ? Number((total / pagamento.parcelas).toFixed(2)) : null,
        vencimento_boleto: pagamento.tipo === 'boleto' ? pagamento.vencimento_boleto : null,
      };
      const payload = {
        ...form,
        forma_pagamento: fp,
        itens: form.itens.map(i => ({ ...i, quantidade: Number(i.quantidade) || 0, valor_unitario: Number(i.valor_unitario) || 0, valor_total: Number(i.valor_total) || 0 }))
      };
      let saved;
      if (isEdit) {
        const res = await api.put(`/proposals/${id}`, payload);
        saved = res.data; toast.success('Orçamento atualizado!');
        setIsDirty(false);
      } else {
        const res = await api.post('/proposals/', payload);
        saved = res.data; toast.success('Orçamento criado!');
        setIsDirty(false);
        navigate(`/proposals/${saved.id}`);
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setLoading(false); }
  };

  const addOcrItems = (ocrItems) => {
    const newItems = ocrItems.map((item, idx) => ({
      position: nextPos + idx, descricao: item.descricao || 'Item do cupom',
      quantidade: Number(item.quantidade) || 1, unidade: item.unidade || 'UN',
      valor_unitario: Number(item.valor_unitario) || 0, valor_total: Number(item.valor_total) || 0,
      tipo: 'avulso', secao: 'material', ref_id: null
    }));
    setNextPos(p => p + ocrItems.length);
    setForm(p => ({ ...p, itens: [...p.itens, ...newItems] }));
  };

  const handleCupomResult = async (items, cupomData) => {
    addOcrItems(items); setCupons(p => [...p, cupomData]); setShowCupom(false);
  };

  const sortedServices = [...form.itens].filter(isService).sort((a, b) => a.position - b.position);
  const sortedMaterials = [...form.itens].filter(i => !isService(i)).sort((a, b) => a.position - b.position);

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">{isEdit ? 'Editar Orçamento' : 'Novo Orçamento'}</h1>
          {isEdit && form.num && <p className="text-sm text-zinc-500">#{form.num}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => safeNavigate('/')} className="px-4 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors">Cancelar</button>
          <button data-testid="save-proposal-btn" onClick={handleSave} disabled={loading}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-xl transition-colors">
            <Save size={16} />
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* Basic Info */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-semibold text-white">Informações gerais</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="field-label">Título / Descrição *</label>
            <input data-testid="titulo-input" required className="field-input" placeholder="Ex: Instalação de CFTV" value={form.titulo} onChange={e => setField('titulo', e.target.value.toUpperCase())} />
          </div>
          <div>
            <label className="field-label">Tipo</label>
            <select className="field-select" value={form.tipo} onChange={e => setField('tipo', e.target.value)}>
              {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Status</label>
            <select data-testid="status-select" className="field-select" value={form.status} onChange={e => setField('status', e.target.value)}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Data de emissão</label>
            <input type="date" className="field-input" value={form.emissao} onChange={e => setField('emissao', e.target.value)} />
          </div>
          <div>
            <label className="field-label">Validade</label>
            <input type="date" className="field-input" value={form.validade} onChange={e => setField('validade', e.target.value)} />
          </div>
          <div>
            <label className="field-label">Prazo de execução (dias)</label>
            <input type="number" min="0" inputMode="numeric" className="field-input" placeholder="Ex: 5" value={form.prazo_execucao} onChange={e => setField('prazo_execucao', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Client */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-semibold text-white">Cliente</h2>
        <div className="relative">
          <label className="field-label">Nome do cliente</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="client-name-input"
                className="field-input pl-9"
                placeholder="Digite ou selecione..."
                value={clientSearch}
                onChange={e => handleClientSearch(e.target.value)}
                onFocus={() => setShowClientSuggestions(true)}
                onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
              />
            </div>
            {'contacts' in navigator && (
              <button onClick={tryContactPicker} title="Buscar na agenda" className="flex items-center justify-center w-10 border border-zinc-700 hover:border-orange-500/50 text-zinc-500 hover:text-orange-400 rounded-xl transition-colors shrink-0">
                <Phone size={16} />
              </button>
            )}
          </div>
          {showClientSuggestions && filteredClients.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-30 bg-zinc-800 border border-zinc-700 rounded-xl mt-1 overflow-hidden shadow-xl max-h-52 overflow-y-auto">
              {filteredClients.map(c => (
                <button key={c.id} onClick={() => selectClient(c)} className="w-full text-left px-4 py-3 hover:bg-zinc-700 transition-colors border-b border-zinc-700/50 last:border-0">
                  <div className="text-sm font-medium text-white">{c.nome}</div>
                  {c.cpf_cnpj && <div className="text-xs text-zinc-500">{c.cpf_cnpj}</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        {showNewClientForm && clientSearch && (
          <div className="bg-zinc-800/50 border border-dashed border-orange-500/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-orange-400 font-medium">Cliente não encontrado — cadastrar novo:</p>
              <button onClick={() => setShowNewClientForm(false)} className="text-zinc-500 hover:text-zinc-300"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="field-label">Nome *</label>
                <input className="field-input" value={clientSearch} onChange={e => { setClientSearch(e.target.value.toUpperCase()); setNewClient(p => ({ ...p, nome: e.target.value.toUpperCase() })); }} />
              </div>
              <div>
                <label className="field-label">Telefone</label>
                <input type="tel" inputMode="tel" className="field-input" placeholder="(99) 99999-9999" value={newClient.tel} onChange={e => setNewClient(p => ({ ...p, tel: e.target.value }))} />
              </div>
              <div>
                <label className="field-label">WhatsApp</label>
                <input type="tel" inputMode="tel" className="field-input" placeholder="(99) 99999-9999" value={newClient.whatsapp} onChange={e => setNewClient(p => ({ ...p, whatsapp: e.target.value }))} />
              </div>
              <div>
                <label className="field-label">E-mail</label>
                <input className="field-input" value={newClient.email} onChange={e => setNewClient(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div>
                <label className="field-label">CPF/CNPJ</label>
                <CpfCnpjInput
                  value={newClient.cpf_cnpj}
                  onChange={(v) => setNewClient(p => ({ ...p, cpf_cnpj: v }))}
                  onlyMode="cpf_cnpj"
                />
              </div>
            </div>
            <div className="flex justify-center">
              <button data-testid="add-client-btn" onClick={saveNewClient}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors">
                <User size={15} />
                Salvar cliente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── SERVIÇO ──────────────────────────────────────────────── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-white">Serviço</h2>
          <button data-testid="add-service-btn" onClick={addServiceItem}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold px-3 py-1.5 rounded-xl transition-colors">
            <Plus size={14} /> Adicionar serviço
          </button>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-4">
          {sortedServices.length === 0 && <div className="text-center py-4 text-zinc-600 text-sm">Nenhum serviço adicionado</div>}
          {sortedServices.map((item, idx) => {
            const results = getServiceResults(item.position);
            return (
              <div key={item.position} className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-blue-400 uppercase tracking-wide">Serviço #{String(item.position).padStart(2, '0')}</span>
                  <button onClick={() => removeItem(item.position)} className="text-zinc-600 hover:text-red-400 transition-colors p-1"><Trash2 size={14} /></button>
                </div>
                <div className="relative">
                  <input className="field-input text-sm" placeholder="Buscar ou digitar serviço..."
                    value={item.descricao}
                    onChange={e => { const v = e.target.value.toUpperCase(); updateItem(item.position, 'descricao', v); setItemSearch(p => ({ ...p, [item.position]: v })); setShowItemSearch(p => ({ ...p, [item.position]: true })); }}
                    onFocus={() => setShowItemSearch(p => ({ ...p, [item.position]: true }))}
                    onBlur={() => setTimeout(() => setShowItemSearch(p => ({ ...p, [item.position]: false })), 200)}
                  />
                  {showItemSearch[item.position] && results.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-30 bg-zinc-800 border border-zinc-700 rounded-xl mt-1 shadow-xl max-h-52 overflow-y-auto">
                      {results.map(r => (
                        <button key={r.id} onClick={() => selectCatalogItem(item.position, r, 'servico', 'servico')} className="w-full text-left px-3 py-2.5 hover:bg-zinc-700 border-b border-zinc-700/50 last:border-0">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-white">{r.descricao}</span>
                            <span className="text-xs text-zinc-400">{brl(r.valor)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {itemSearch[item.position] && results.length === 0 && !item.ref_id && (
                    <div className="flex items-center justify-between gap-2 mt-1.5 text-xs">
                      <span className="text-amber-400">Serviço inexistente</span>
                      <button type="button" onClick={() => openCatalogModal(item.position, 'servico')}
                        className="flex items-center gap-1 text-orange-400 hover:text-orange-300 font-medium">
                        <Plus size={12} /> Cadastrar
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">QTD</label>
                    <input type="text" inputMode="decimal" className="field-input text-sm text-right" placeholder="0" value={item.quantidade || ''} onChange={e => updateItem(item.position, 'quantidade', e.target.value)} /></div>
                  <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">UNIDADE</label>
                    <input className="field-input text-sm text-center" value={item.unidade} onChange={e => updateItem(item.position, 'unidade', e.target.value.toUpperCase())} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">VALOR UNIT</label>
                    <input type="number" min="0" step="0.01" inputMode="decimal" className="field-input text-sm text-right" placeholder="0,00" value={item.valor_unitario} onChange={e => updateItem(item.position, 'valor_unitario', e.target.value)} /></div>
                  <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">TOTAL</label>
                    <div className="field-input text-sm text-right bg-zinc-700/30 text-orange-400 font-mono font-medium">{fmt2(item.valor_total)}</div></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop */}
        <div className="hidden md:block">
          {sortedServices.length > 0 && (
            <div className="overflow-visible">
              <table className="w-full text-sm">
                <thead><tr className="text-zinc-500 text-xs border-b border-zinc-800">
                  <th className="text-left py-2 px-2 w-10">Nº</th>
                  <th className="text-left py-2 px-2">Descrição do serviço</th>
                  <th className="text-right py-2 px-2 w-20">Qtd</th>
                  <th className="text-left py-2 px-2 w-16">Un</th>
                  <th className="text-right py-2 px-2 w-28">Valor Unit.</th>
                  <th className="text-right py-2 px-2 w-28">Total</th>
                  <th className="w-8"></th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-800/50">
                  {sortedServices.map(item => {
                    const results = getServiceResults(item.position);
                    return (
                      <tr key={item.position} className="group">
                        <td className="py-2 px-2"><span className="font-mono text-xs text-zinc-500">{String(item.position).padStart(2, '0')}</span></td>
                        <td className="py-2 px-2">
                          <div className="relative">
                            <input className="field-input text-xs py-1.5" placeholder="Buscar serviço..."
                              value={item.descricao}
                              onChange={e => { const v = e.target.value.toUpperCase(); updateItem(item.position, 'descricao', v); setItemSearch(p => ({ ...p, [item.position]: v })); setShowItemSearch(p => ({ ...p, [item.position]: true })); }}
                              onFocus={() => setShowItemSearch(p => ({ ...p, [item.position]: true }))}
                              onBlur={() => setTimeout(() => setShowItemSearch(p => ({ ...p, [item.position]: false })), 200)}
                            />
                            {showItemSearch[item.position] && results.length > 0 && (
                              <div className="absolute top-full left-0 right-0 z-30 bg-zinc-800 border border-zinc-700 rounded-xl mt-1 shadow-xl max-h-52 overflow-y-auto">
                                {results.map(r => (
                                  <button key={r.id} onClick={() => selectCatalogItem(item.position, r, 'servico', 'servico')} className="w-full text-left px-3 py-2.5 hover:bg-zinc-700 border-b border-zinc-700/50 last:border-0">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-medium text-white">{r.descricao}</span>
                                      <span className="text-xs text-zinc-400">{brl(r.valor)}</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                            {itemSearch[item.position] && results.length === 0 && !item.ref_id && (
                              <div className="flex items-center gap-2 mt-1 text-[11px] whitespace-nowrap">
                                <span className="text-amber-400">Serviço inexistente</span>
                                <button type="button" onClick={() => openCatalogModal(item.position, 'servico')}
                                  className="flex items-center gap-0.5 text-orange-400 hover:text-orange-300 font-medium">
                                  <Plus size={11} /> Cadastrar
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-2"><input type="text" inputMode="decimal" className="field-input text-xs py-1.5 text-right w-full" placeholder="0" value={item.quantidade || ''} onChange={e => updateItem(item.position, 'quantidade', e.target.value)} /></td>
                        <td className="py-2 px-2"><input className="field-input text-xs py-1.5 text-center w-full" value={item.unidade} onChange={e => updateItem(item.position, 'unidade', e.target.value.toUpperCase())} /></td>
                        <td className="py-2 px-2"><input type="number" min="0" step="0.01" inputMode="decimal" className="field-input text-xs py-1.5 text-right w-full" placeholder="0,00" value={item.valor_unitario} onChange={e => updateItem(item.position, 'valor_unitario', e.target.value)} /></td>
                        <td className="py-2 px-2 text-right"><span className="font-mono text-xs text-white">{brl(item.valor_total)}</span></td>
                        <td className="py-2 px-2"><button onClick={() => removeItem(item.position)} className="text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {sortedServices.length === 0 && <div className="text-center py-6 text-zinc-600 text-sm">Nenhum serviço adicionado. Clique em "Adicionar serviço".</div>}
        </div>
      </div>

      {/* ── ITENS / MATERIAIS ─────────────────────────────────────── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading font-semibold text-white">Itens do orçamento</h2>
          <div className="flex gap-2">
            <button data-testid="capture-cupom-btn" onClick={() => setShowCupom(true)}
              className="flex items-center gap-2 border border-zinc-700 hover:border-orange-500/50 hover:bg-orange-500/10 text-zinc-300 hover:text-orange-400 text-sm px-3 py-1.5 rounded-lg transition-colors">
              <Camera size={15} /> Capturar Cupom
            </button>
            <button data-testid="add-item-btn" onClick={addMaterialItem}
              className="flex items-center gap-2 border border-orange-500/40 hover:bg-orange-500/10 text-orange-400 text-sm px-3 py-1.5 rounded-lg transition-colors">
              <Plus size={14} /> Adicionar item
            </button>
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-4">
          {sortedMaterials.length === 0 && <div className="text-center py-4 text-zinc-600 text-sm">Nenhum material adicionado</div>}
          {sortedMaterials.map((item, idx) => {
            const results = getMaterialResults(item.position);
            return (
              <div key={item.position} className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-green-400 uppercase tracking-wide">Item #{String(item.position).padStart(2, '0')}</span>
                  <button onClick={() => removeItem(item.position)} className="text-zinc-600 hover:text-red-400 transition-colors p-1"><Trash2 size={14} /></button>
                </div>
                <div className="relative">
                  <input className="field-input text-sm" placeholder="Buscar ou digitar material..."
                    value={item.descricao}
                    onChange={e => { const v = e.target.value.toUpperCase(); updateItem(item.position, 'descricao', v); setItemSearch(p => ({ ...p, [item.position]: v })); setShowItemSearch(p => ({ ...p, [item.position]: true })); }}
                    onFocus={() => setShowItemSearch(p => ({ ...p, [item.position]: true }))}
                    onBlur={() => setTimeout(() => setShowItemSearch(p => ({ ...p, [item.position]: false })), 200)}
                  />
                  {showItemSearch[item.position] && results.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-30 bg-zinc-800 border border-zinc-700 rounded-xl mt-1 shadow-xl max-h-52 overflow-y-auto">
                      {results.map(r => (
                        <button key={r.id} onClick={() => selectCatalogItem(item.position, r, 'produto', 'material')} className="w-full text-left px-3 py-2.5 hover:bg-zinc-700 border-b border-zinc-700/50 last:border-0">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-white">{r.descricao}</span>
                            <span className="text-xs text-zinc-400">{brl(r.valor)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {itemSearch[item.position] && results.length === 0 && !item.ref_id && (
                    <div className="flex items-center justify-between gap-2 mt-1.5 text-xs">
                      <span className="text-amber-400">Produto inexistente</span>
                      <button type="button" onClick={() => openCatalogModal(item.position, 'material')}
                        className="flex items-center gap-1 text-orange-400 hover:text-orange-300 font-medium">
                        <Plus size={12} /> Cadastrar
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">QTD</label>
                    <input type="text" inputMode="decimal" className="field-input text-sm text-right" placeholder="0" value={item.quantidade || ''} onChange={e => updateItem(item.position, 'quantidade', e.target.value)} /></div>
                  <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">UNIDADE</label>
                    <input className="field-input text-sm text-center" value={item.unidade} onChange={e => updateItem(item.position, 'unidade', e.target.value.toUpperCase())} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">VALOR UNIT</label>
                    <input type="number" min="0" step="0.01" inputMode="decimal" className="field-input text-sm text-right" placeholder="0,00" value={item.valor_unitario} onChange={e => updateItem(item.position, 'valor_unitario', e.target.value)} /></div>
                  <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">TOTAL</label>
                    <div className="field-input text-sm text-right bg-zinc-700/30 text-orange-400 font-mono font-medium">{fmt2(item.valor_total)}</div></div>
                </div>
                {idx === sortedMaterials.length - 1 && (
                  <button data-testid="add-item-mobile-btn" onClick={addMaterialItem}
                    className="w-full flex items-center justify-center gap-2 border border-dashed border-orange-500/40 hover:bg-orange-500/10 text-orange-400 text-sm py-2.5 rounded-xl transition-colors mt-2">
                    <Plus size={16} /> Adicionar próximo item
                  </button>
                )}
              </div>
            );
          })}
          {sortedMaterials.length === 0 && (
            <button data-testid="add-item-btn-mobile" onClick={addMaterialItem}
              className="w-full flex items-center justify-center gap-2 border border-dashed border-orange-500/40 hover:bg-orange-500/10 text-orange-400 text-sm py-3 rounded-xl transition-colors">
              <Plus size={16} /> Adicionar item
            </button>
          )}
        </div>

        {/* Desktop */}
        <div className="hidden md:block">
          {sortedMaterials.length > 0 && (
            <div className="overflow-visible">
              <table className="w-full text-sm">
                <thead><tr className="text-zinc-500 text-xs border-b border-zinc-800">
                  <th className="text-left py-2 px-2 w-10">Nº</th>
                  <th className="text-left py-2 px-2">Descrição do material</th>
                  <th className="text-right py-2 px-2 w-20">Qtd</th>
                  <th className="text-left py-2 px-2 w-16">Un</th>
                  <th className="text-right py-2 px-2 w-28">Valor Unit.</th>
                  <th className="text-right py-2 px-2 w-28">Total</th>
                  <th className="w-8"></th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-800/50">
                  {sortedMaterials.map(item => {
                    const results = getMaterialResults(item.position);
                    return (
                      <tr key={item.position} className="group">
                        <td className="py-2 px-2"><span className="font-mono text-xs text-zinc-500">{String(item.position).padStart(2, '0')}</span></td>
                        <td className="py-2 px-2">
                          <div className="relative">
                            <input className="field-input text-xs py-1.5" placeholder="Buscar material/produto..."
                              value={item.descricao}
                              onChange={e => { const v = e.target.value.toUpperCase(); updateItem(item.position, 'descricao', v); setItemSearch(p => ({ ...p, [item.position]: v })); setShowItemSearch(p => ({ ...p, [item.position]: true })); }}
                              onFocus={() => setShowItemSearch(p => ({ ...p, [item.position]: true }))}
                              onBlur={() => setTimeout(() => setShowItemSearch(p => ({ ...p, [item.position]: false })), 200)}
                            />
                            {showItemSearch[item.position] && results.length > 0 && (
                              <div className="absolute top-full left-0 right-0 z-30 bg-zinc-800 border border-zinc-700 rounded-xl mt-1 shadow-xl max-h-52 overflow-y-auto">
                                {results.map(r => (
                                  <button key={r.id} onClick={() => selectCatalogItem(item.position, r, 'produto', 'material')} className="w-full text-left px-3 py-2.5 hover:bg-zinc-700 border-b border-zinc-700/50 last:border-0">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-medium text-white">{r.descricao}</span>
                                      <span className="text-xs text-zinc-400">{brl(r.valor)}</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                            {itemSearch[item.position] && results.length === 0 && !item.ref_id && (
                              <div className="flex items-center gap-2 mt-1 text-[11px] whitespace-nowrap">
                                <span className="text-amber-400">Produto inexistente</span>
                                <button type="button" onClick={() => openCatalogModal(item.position, 'material')}
                                  className="flex items-center gap-0.5 text-orange-400 hover:text-orange-300 font-medium">
                                  <Plus size={11} /> Cadastrar
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-2"><input type="text" inputMode="decimal" className="field-input text-xs py-1.5 text-right w-full" placeholder="0" value={item.quantidade || ''} onChange={e => updateItem(item.position, 'quantidade', e.target.value)} /></td>
                        <td className="py-2 px-2"><input className="field-input text-xs py-1.5 text-center w-full" value={item.unidade} onChange={e => updateItem(item.position, 'unidade', e.target.value.toUpperCase())} /></td>
                        <td className="py-2 px-2"><input type="number" min="0" step="0.01" inputMode="decimal" className="field-input text-xs py-1.5 text-right w-full" placeholder="0,00" value={item.valor_unitario} onChange={e => updateItem(item.position, 'valor_unitario', e.target.value)} /></td>
                        <td className="py-2 px-2 text-right"><span className="font-mono text-xs text-white">{brl(item.valor_total)}</span></td>
                        <td className="py-2 px-2"><button onClick={() => removeItem(item.position)} className="text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {sortedMaterials.length === 0 && <div className="text-center py-6 text-zinc-600 text-sm">Nenhum material adicionado. Clique em "Adicionar item".</div>}
        </div>

        {/* Cupons */}
        {cupons.length > 0 && (
          <div className="border-t border-zinc-800 pt-4">
            <p className="text-xs text-zinc-500 mb-2">Cupons anexados ({cupons.length})</p>
            <div className="flex gap-2 flex-wrap">
              {cupons.map(c => (
                <div key={c.id} className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
                  <Camera size={12} />{c.nome_arquivo || 'Cupom'}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── TOTAIS ────────────────────────────────────────────────── */}
      {form.itens.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-2">
          <h2 className="font-heading font-semibold text-white text-sm mb-3">Resumo de valores</h2>
          {totalServicos > 0 && <div className="flex justify-between text-sm text-zinc-400"><span>Valor total dos serviços</span><span className="font-mono">{brl(totalServicos)}</span></div>}
          {totalMateriais > 0 && <div className="flex justify-between text-sm text-zinc-400"><span>Valor total dos materiais</span><span className="font-mono">{brl(totalMateriais)}</span></div>}
          {(totalServicos > 0 || totalMateriais > 0) && <div className="border-t border-zinc-800 pt-2 flex justify-between text-sm text-zinc-400"><span>Subtotal</span><span className="font-mono">{brl(subtotal)}</span></div>}
          <div className="flex items-center justify-between text-sm text-zinc-400">
            <span>Desconto</span>
            <div className="flex items-center gap-2">
              <span className="text-zinc-500">R$</span>
              <input type="number" min="0" step="0.01" className="w-28 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-right text-sm text-white focus:outline-none focus:border-orange-500"
                value={form.desconto} onChange={e => setField('desconto', Number(e.target.value))} />
            </div>
          </div>
          <div className="flex justify-between text-base font-bold text-white border-t border-zinc-800 pt-2 mt-2">
            <span>Total</span>
            <span className="font-mono text-orange-400">{brl(total)}</span>
          </div>
        </div>
      )}

      {/* Payment method */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-semibold text-white">Forma de pagamento</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {PAGAMENTO_TIPOS.map(pt => (
            <button key={pt.value} onClick={() => setPagamento(p => ({ ...p, tipo: pt.value }))}
              className={`py-2.5 px-3 rounded-xl text-sm font-medium border transition-all ${pagamento.tipo === pt.value ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}>
              {pt.label}
            </button>
          ))}
        </div>
        {pagamento.tipo === 'cartao_parcelado' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Nº de parcelas</label>
                <input type="number" min="2" max="48" className="field-input"
                  value={pagamento.parcelas} onChange={e => setPagamento(p => ({ ...p, parcelas: Number(e.target.value) }))} />
              </div>
              <div>
                <label className="field-label">Valor por parcela</label>
                <div className="field-input bg-zinc-800/50 text-right font-mono text-orange-400">
                  {pagamento.parcelas > 0 ? brl(valorParcela) : '—'}
                </div>
              </div>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-3 text-sm text-zinc-400">
              <span className="font-medium text-white">{pagamento.parcelas}x</span> de <span className="text-orange-400 font-mono font-medium">{brl(valorParcela)}</span> · Total: <span className="font-mono">{brl(total)}</span>
            </div>
          </div>
        )}
        {pagamento.tipo === 'boleto' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Nº de parcelas (1 a 5)</label>
                <select data-testid="boleto-parcelas-select" className="field-select" value={pagamento.parcelas} onChange={e => setPagamento(p => ({ ...p, parcelas: Number(e.target.value) }))}>
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}x</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Data de vencimento</label>
                <input type="date" className="field-input" value={pagamento.vencimento_boleto} onChange={e => setPagamento(p => ({ ...p, vencimento_boleto: e.target.value }))} />
              </div>
            </div>
            {pagamento.parcelas > 1 && (
              <div className="bg-zinc-800/50 rounded-xl p-3 text-sm text-zinc-400">
                <span className="font-medium text-white">{pagamento.parcelas}x</span> de <span className="text-orange-400 font-mono font-medium">{brl(total / pagamento.parcelas)}</span> · Total: <span className="font-mono">{brl(total)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Garantia dos Serviços — editável por orçamento */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-white">Garantia dos Serviços</h2>
          <span className="text-xs text-zinc-600 italic">Específico deste orçamento</span>
        </div>
        <div>
          <label className="field-label">Prazo de garantia</label>
          <input
            className="field-input"
            placeholder="Ex: 90 dias, 6 meses, 1 ano"
            value={form.garantia_prazo}
            onChange={e => setField('garantia_prazo', e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">Condições de garantia</label>
          <textarea
            className="field-input min-h-[80px] resize-y"
            rows={3}
            placeholder="Ex: Garantia cobre defeitos de instalação. Não inclui danos por mau uso..."
            value={form.garantia_condicoes}
            onChange={e => setField('garantia_condicoes', e.target.value)}
          />
        </div>
      </div>

      {/* Observations */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
        <h2 className="font-heading font-semibold text-white">Observações</h2>
        <textarea className="field-input min-h-[80px] resize-y" rows={3} placeholder="Condições, prazo de entrega, garantias..." value={form.obs} onChange={e => setField('obs', e.target.value.toUpperCase())} />
      </div>

      {/* Signature */}
      {isEdit && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-white">Assinatura do cliente</h2>
            <button onClick={() => setShowSignature(true)} className="text-sm text-orange-400 hover:text-orange-300 border border-orange-500/30 hover:bg-orange-500/10 px-3 py-1.5 rounded-lg transition-colors">
              {form.assinatura_img ? 'Refazer assinatura' : 'Coletar assinatura'}
            </button>
          </div>
          {form.assinatura_img && <img src={form.assinatura_img} alt="Assinatura" className="max-h-24 border border-zinc-700 rounded-lg bg-white p-2" />}
        </div>
      )}

      {showCupom && <CupomCapture proposalId={proposalId} onClose={() => setShowCupom(false)} onItemsAdded={handleCupomResult} />}
      {showSignature && (
        <SignaturePad onClose={() => setShowSignature(false)} onSave={(sigImg) => { setForm(p => ({ ...p, assinatura_img: sigImg })); setShowSignature(false); toast.success('Assinatura capturada!'); }} />
      )}

      <CatalogItemModal
        open={catalogModal.open}
        kind={catalogModal.kind}
        editing={null}
        initialDescricao={catalogModal.descricao}
        categories={categories}
        onSave={saveCatalogItem}
        onClose={() => setCatalogModal(p => ({ ...p, open: false }))}
        onSaved={handleCatalogSaved}
      />

      {/* Save button at bottom */}
      <div className="flex gap-3 pb-6">
        <button onClick={() => safeNavigate('/')} className="flex-1 py-3 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors">
          Cancelar
        </button>
        <button data-testid="save-proposal-bottom-btn" onClick={handleSave} disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors">
          <Save size={16} />
          {loading ? 'Salvando...' : 'Salvar Orçamento'}
        </button>
      </div>

      {/* Unsaved changes dialog */}
      {showUnsavedDialog && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-2xl">
            <h3 className="text-white font-semibold text-lg">Sair sem salvar?</h3>
            <p className="text-zinc-400 text-sm">Você tem alterações não salvas. Se sair agora, perderá todas as mudanças.</p>
            <div className="flex gap-3">
              <button data-testid="blocker-stay-btn" onClick={() => setShowUnsavedDialog(false)} className="flex-1 border border-zinc-700 text-zinc-300 py-2.5 rounded-xl text-sm hover:bg-zinc-800 transition-colors">
                Continuar editando
              </button>
              <button data-testid="blocker-leave-btn" onClick={confirmLeave} className="flex-1 bg-red-500 hover:bg-red-400 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors">
                Sair sem salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
